// CADINU DEX palette — dark-first, derived from apps-dex/design/tokens.json (2026-09-28).
// Every text/background pair used as small text clears WCAG 4.5:1 (measured; see
// apps-dex/design/DEX-DESIGN-TOKENS-20260928.md §1.2 and prediction/docs/STATUS.md "DEX shell").
// Raw brand ink (#2100FF) and violet (#7E00FE) are chrome/logo colours only: `gradientBold`,
// focus ring, the header wordmark. `secondary` (used as small text all over the app) is the
// accessible stand-in `accent-text` #8C7CFF in dark, a deepened violet in light.

export const baseColors = {
  white: '#FFFFFF',
  // sell / negative PnL / error (games globals.css --danger)
  failure: '#FF6B7A',
  failure33: '#FF6B7A33',
  // interactive cyan "ice" — links, active states, primary control fill
  primary: '#7FD4E8',
  primary0f: '#7FD4E80f',
  primary3D: '#7FD4E83D',
  primaryBright: '#A6E6F4',
  primaryDark: '#4FA9C2',
  // buy / positive PnL / confirmed — deliberately not the Games aurora green
  success: '#39D98A',
  success19: '#39D98A19',
  // pending / caution / slippage (games globals.css --warn)
  warning: '#FFB35C',
  warning2D: '#FFB35C2D',
  warning33: '#FFB35C33',
  // text colour for anything drawn ON a filled primary/success/failure/warning surface
  textOnFill: '#050713',
}

export const additionalColors = {
  binance: '#F0B90B',
  overlay: 'rgba(5, 7, 19, 0.78)',
  // value only — CBON, rewards, claim CTAs
  gold: '#FFC24A',
  silver: '#B2B2B2',
  bronze: '#E7974D',
}

export const lightColors = {
  ...baseColors,
  ...additionalColors,
  // light is secondary: same hues, deepened so they still pass on white
  primary: '#0A6F88',
  primary0f: '#0A6F880f',
  primary3D: '#0A6F883D',
  primaryBright: '#7FD4E8',
  primaryDark: '#075A6E',
  success: '#178A55',
  success19: '#178A5519',
  failure: '#D6334A',
  failure33: '#D6334A33',
  warning: '#B35F00',
  warning2D: '#B35F002D',
  warning33: '#B35F0033',
  gold: '#8A5A00',
  textOnFill: '#FFFFFF',
  secondary: '#5B12C9',
  secondary80: '#5B12C980',
  background: '#F4F6FF',
  backgroundDisabled: '#E3E7F3',
  backgroundAlt: '#FFFFFF',
  backgroundAlt2: 'rgba(255, 255, 255, 0.78)',
  cardBorder: '#D9DFF0',
  contrast: '#0A0E22',
  dropdown: '#F7F8FD',
  dropdownDeep: '#EEF1FA',
  invertedContrast: '#FFFFFF',
  input: '#EEF2FB',
  inputSecondary: '#D9DFF0',
  tertiary: '#EEF2FB',
  text: '#0A0E22',
  text99: '#0A0E2299',
  textDisabled: '#8C95AB',
  textSubtle: '#4B5677',
  disabled: '#E3E7F3',
  gradientBubblegum: 'linear-gradient(139.73deg, #EEF6FF 0%, #F1EEFF 100%)',
  gradientInverseBubblegum: 'linear-gradient(139.73deg, #F1EEFF 0%, #EEF6FF 100%)',
  gradientCardHeader: 'linear-gradient(111.68deg, #F1EEFF 0%, #E8F2F6 100%)',
  gradientBlue: 'linear-gradient(180deg, #CDEFF7 0%, #A6E6F4 100%)',
  gradientViolet: 'linear-gradient(180deg, #E6D9FF 0%, #CDB8FA 100%)',
  gradientVioletAlt: 'linear-gradient(180deg, #DDE3F5 0%, #B8BEE6 100%)',
  gradientGold: 'linear-gradient(180deg, #FFE08A 0%, #FFC24A 100%)',
  gradientBold: 'linear-gradient(180deg, #2100FF 0%, #7E00FE 100%)',
}

export const darkColors = {
  ...baseColors,
  ...additionalColors,
  // accent-text: the only accessible way to show the ink/violet hue as small text (6.16:1 on night)
  secondary: '#8C7CFF',
  secondary80: '#8C7CFF80',
  background: '#050713', // night
  backgroundDisabled: '#171B2E',
  backgroundAlt: '#0A0E22', // night-2: card, modal, table row
  backgroundAlt2: 'rgba(10, 14, 34, 0.78)',
  cardBorder: 'rgba(180, 200, 255, 0.18)', // glass-line
  contrast: '#F4F6FF',
  dropdown: '#0D1230',
  dropdownDeep: '#050713',
  invertedContrast: '#050713',
  input: 'rgba(127, 212, 232, 0.10)',
  inputSecondary: 'rgba(127, 212, 232, 0.16)',
  tertiary: '#11162C',
  text: '#F4F6FF', // snow
  text99: '#F4F6FF99',
  textDisabled: '#6B7590',
  textSubtle: '#C9D2E6', // snow-2
  disabled: '#2A3050',
  gradientBubblegum: 'linear-gradient(139.73deg, #0F1330 0%, #0A0E22 100%)',
  gradientInverseBubblegum: 'linear-gradient(139.73deg, #0A0E22 0%, #0F1330 100%)',
  gradientCardHeader: 'linear-gradient(166.77deg, #0F1330 0%, #0A0E22 100%)',
  gradientBlue: 'linear-gradient(180deg, #123047 0%, #0A0E22 100%)',
  gradientViolet: 'linear-gradient(180deg, #2B1450 0%, #0A0E22 100%)',
  gradientVioletAlt: 'linear-gradient(180deg, #1E2450 0%, #0A0E22 100%)',
  gradientGold: 'linear-gradient(180deg, #FFE08A 0%, #FFC24A 100%)',
  // THE wordmark gradient — one hero element (Connect Wallet CTA / header logo), nowhere else
  gradientBold: 'linear-gradient(180deg, #2100FF 0%, #7E00FE 100%)',
}
